// src/services/office/factory.ts
import { OutlookAdapter, DocumentAdapter } from "./adapters";
import { IOfficeContext, HostType } from "../../types/office";
import { logger } from "../../utils/logger";

export function createOfficeContext(host: HostType, userEmail?: string): IOfficeContext | null {
  logger.debug(`[Factory] Initializing adapter for host: ${host}`);
  logger.debug(`[Factory] User email provided:`, !!userEmail);
  logger.debug(`[Factory] Office.context available:`, !!Office.context);
  logger.debug(`[Factory] Office.context.document available:`, !!Office.context?.document);

  if (host === "Outlook") {
    logger.debug(`[Factory] Outlook host detected, checking mailbox...`);
    logger.debug(`[Factory] Office.context.mailbox available:`, !!Office.context.mailbox);
    const item = Office.context.mailbox?.item;
    logger.debug(`[Factory] Outlook item retrieved:`, !!item);
    if (item) {
      logger.debug(`[Factory] Creating OutlookAdapter...`);
      try {
        const adapter = new OutlookAdapter(item, userEmail);
        logger.debug(`[Factory] OutlookAdapter created successfully`);
        return adapter;
      } catch (error) {
        logger.error(`[Factory] Error creating OutlookAdapter:`, error);
        throw error;
      }
    }
    logger.warn(`[Factory] No Outlook item found, returning null`);
    return null;
  }

  if (host === "Word" || host === "Excel" || host === "PowerPoint") {
    logger.debug(`[Factory] Document host detected (${host}), creating DocumentAdapter...`);
    logger.debug(`[Factory] Office.context.document:`, {
      available: !!Office.context?.document,
      hasUrl: !!Office.context?.document?.url,
      mode: Office.context?.document?.mode || "N/A",
    });

    if (host === "Word") {
      logger.debug(`[Factory] Word-specific checks:`);
      logger.debug(`[Factory] - typeof Word:`, typeof Word);
      logger.debug(`[Factory] - Word available:`, typeof Word !== "undefined");
      logger.debug(
        `[Factory] - Office.context.document.getSelectedDataAsync:`,
        typeof Office.context?.document?.getSelectedDataAsync
      );
    }

    try {
      const adapter = new DocumentAdapter(host, userEmail);
      logger.debug(`[Factory] DocumentAdapter created successfully for ${host}`);
      return adapter;
    } catch (error) {
      logger.error(`[Factory] Error creating DocumentAdapter for ${host}:`, error);
      logger.error(`[Factory] DocumentAdapter error details:`, {
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        host,
        hasUserEmail: !!userEmail,
      });
      throw error;
    }
  }

  logger.warn(`[Factory] Unknown host type: ${host}, returning null`);
  return null;
}
